/* lib/api/client.js — боевой сетевой примитив (порт из frontend/src/lib/api/client.js, 1:1).
 *
 * Контракт apiFetch: ВСЕГДА резолвится в { ok, status, data } и НЕ бросает по HTTP-кодам
 * (data = распарсенный JSON или null). Это ЗЕРКАЛО мок-контракта fakeApi (тот возвращает
 * payload и бросает err{status,code,detail}); конвертацию делает lib/api/index.js → unwrap().
 *
 * - CSRF: cookie csrftoken → заголовок X-CSRFToken для не-GET/HEAD; ensureCsrf() прогревает cookie.
 * - Workspace: localStorage['restorium.active_workspace_id'] → заголовок X-Workspace-Id
 *   (кроме запросов, переписанных на публичный демо-слой, — см. demoRewrite).
 * - credentials:'same-origin' → сессионная кука ходит ТОЛЬКО через Vite-proxy (см. vite.config.js).
 * - FormData уходит как есть (multipart); остальное — JSON.
 */

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : null;
}

const WORKSPACE_KEY = 'restorium.active_workspace_id';

/* Сырое значение активного пространства. Браузер с заблокированным хранилищем
 * сайта бросает SecurityError на САМОМ обращении к window.localStorage — без
 * try это роняло apiFetch до запроса, и каждая вкладка публичного демо-стенда
 * (куда такие посетители и приходят) показывала ошибку. Нет доступа = нет выбора. */
function readWorkspaceRaw() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(WORKSPACE_KEY);
  } catch {
    return null;
  }
}

export function getActiveWorkspaceId() {
  const raw = readWorkspaceRaw();
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function setActiveWorkspaceId(id) {
  if (typeof window === 'undefined') return;
  if (id == null) {
    window.localStorage.removeItem(WORKSPACE_KEY);
    return;
  }
  window.localStorage.setItem(WORKSPACE_KEY, String(id));
}

// Публичный демо-стенд (/business-demo) отдаёт iiko-аналитику read-only и БЕЗ
// сессии через отдельный AllowAny-слой /api/demo/iiko/*. Пока открыт демо-маршрут,
// переписываем ТОЛЬКО запросы интеграций на демо-эндпоинты — экраны отчётов
// переиспользуются как есть, а всё прочее (csrf, auth) не трогаем. Ключуемся по
// текущему пути (не по мутабельному флагу), поэтому переписывание самоограничено
// демо-страницей и сбрасывается при уходе с неё.
const DEMO_PREFIX = '/api/integrations/';

/* Открыт ли сейчас публичный демо-стенд. Единственная реализация проверки пути
 * в сетевом слое: на ней стоят и переписывание запросов, и область хранения
 * ниже. Семантика обязана совпадать с isDemoRoute() демо-моста экранов
 * (screens/demo/demoAccess.jsx) — это держит тест lib/api/demoScope.test.js. */
export function isDemoPath() {
  return typeof window !== 'undefined' && !!window.location
    && typeof window.location.pathname === 'string'
    && window.location.pathname.startsWith('/business-demo');
}

export function demoRewrite(path) {
  if (!isDemoPath()) return path;
  if (typeof path === 'string' && path.startsWith(DEMO_PREFIX)) {
    return '/api/demo/iiko/' + path.slice(DEMO_PREFIX.length);
  }
  return path;
}

/* Область хранения удобств Аналитики одного зрителя: столбцы таблиц, период,
 * ставка эквайринга (ключи вида restorium.table.<область>.<таблица>).
 *
 * Демо-стенд — СВОЯ область и никогда не берёт чужую. Демо-страница не трогает
 * restorium.active_workspace_id (гость без сессии, его сбрасывает только выход),
 * поэтому менеджер, показывающий демо из своего браузера, получал в демо скрытые
 * столбцы и период своего живого ресторана, а переключив что-то в демо — молча
 * перекраивал их у себя. Период при этом ещё и врал: диапазон живого ресторана
 * обрезался границами демо-данных, и «90 дней» показывали пару недель.
 *
 * Вне демо — сырое значение пространства или 'x', байт-в-байт как прежние
 * ключи: сохранённые настройки пользователей не теряются. */
export function analyticsStorageScope() {
  if (isDemoPath()) return 'demo';
  return readWorkspaceRaw() || 'x';
}

export async function apiFetch(path, { method = 'GET', body, headers, ...rest } = {}) {
  const target = demoRewrite(path);
  // Публичная демо-страница анонимна и ни одному её запросу пространство не нужно:
  // ни переписанным отчётам, ни заявке (/api/demo/lead/), ни CSRF. Поэтому признак —
  // сама страница, а не «запрос переписан»: иначе заявка с демо уносила бы id живого
  // пространства посетителя, открывшего стенд из своего браузера.
  const demo = isDemoPath();
  path = target;
  const finalHeaders = { Accept: 'application/json', ...headers };
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const token = getCsrfToken();
    if (token) finalHeaders['X-CSRFToken'] = token;
  }
  // Активный workspace — бэк fallback'нёт на personal, если заголовка нет
  // или id не валиден. Передаём всегда, когда выбор уже зафиксирован.
  const wsId = demo ? null : getActiveWorkspaceId();
  if (wsId && finalHeaders['X-Workspace-Id'] === undefined) {
    finalHeaders['X-Workspace-Id'] = String(wsId);
  }

  const res = await fetch(path, {
    method,
    headers: finalHeaders,
    credentials: 'same-origin',
    body: body !== undefined ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
    ...rest,
  });

  let data = null;
  if (res.status !== 204) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { data = await res.json(); } catch { /* noop */ }
    }
  }

  return { ok: res.ok, status: res.status, data };
}

export async function ensureCsrf() {
  if (!getCsrfToken()) {
    await apiFetch('/api/auth/csrf/');
  }
}

/* Имя файла из Content-Disposition: сперва RFC 5987 filename*=UTF-8''…,
 * затем обычный filename="…" / filename=…; null, если заголовка нет. */
export function filenameFromDisposition(header) {
  if (!header) return null;
  const star = header.match(/filename\*\s*=\s*utf-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* noop */ }
  }
  const plain = header.match(/filename\s*=\s*"([^"]*)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  const name = plain ? plain[1].trim() : null;
  return name || null;
}

/* Скачивание файла (docx/zip/pdf/xlsx) БЕЗ window.open: браузерная навигация
 * не шлёт X-Workspace-Id, бэк фолбэчится на personal-workspace и сущности
 * business-пространств ловят 404 (скачивался «битый» файл вместо ошибки).
 * Качаем fetch'ем с теми же заголовками, что apiFetch, и сохраняем blob.
 * Контракт зеркалит apiFetch: { ok, status, data } без throw; data — JSON
 * тела ошибки (для тоста) или null. filename-опция форсирует имя, иначе
 * берём его из Content-Disposition ответа.
 *
 * method/body — для выгрузок, чьи параметры не помещаются в адрес (выбор групп
 * ABC, см. lib/api/urlBudget.js). Это по-прежнему чтение, просто параметры едут
 * телом; сессионная авторизация DRF на не-GET требует CSRF — греем куку и шлём
 * заголовок, как это делает apiFetch. */
export async function downloadFile(path, { filename, method = 'GET', body } = {}) {
  const target = demoRewrite(path);
  const headers = {};
  // демо-страница — без пространства, по тому же признаку, что и apiFetch
  const wsId = isDemoPath() ? null : getActiveWorkspaceId();
  if (wsId) headers['X-Workspace-Id'] = String(wsId);
  if (method !== 'GET' && method !== 'HEAD') {
    await ensureCsrf();
    const token = getCsrfToken();
    if (token) headers['X-CSRFToken'] = token;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(target, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let data = null;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
      try { data = await res.json(); } catch { /* noop */ }
    }
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const name = filename
    || filenameFromDisposition(res.headers.get('content-disposition'))
    || 'download';
  const objUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objUrl);
  return { ok: true, status: res.status, data: null };
}
